/**
 * Molecule Weight Loss Assessment engine
 * State, navigation, validation, persistence, render orchestration
 */
(function () {
  'use strict';

  var config = null;
  var root = null;
  var state = {
    pageIndex: 0,
    answers: {},
    history: [],
    uploads: {},
    guidelinesModal: null,
    rangeModal: null,
    cameraActive: false,
    cameraFailed: false,
    pageError: '',
    uploadError: '',
    submitError: '',
    submitSuccess: '',
    submitting: false,
    submitted: false
  };

  function UI() {
    return window.WmAssessmentUI;
  }

  function mergeOverrides() {
    var o = window.__wmAssessmentOverrides || {};
    config = Object.assign({}, window.wmAssessmentConfig || {});
    if (o.successRedirectUrl) config.successRedirectUrl = o.successRedirectUrl;
    if (typeof o.disableSuccessRedirect === 'boolean') {
      config.disableSuccessRedirect = o.disableSuccessRedirect;
    }
    if (o.api) config.api = Object.assign({}, config.api || {}, o.api);
    if (o.customerEmailFallback && !state.answers.email) {
      state.answers.email = o.customerEmailFallback;
    }
    if (o.customerFirstNameFallback && !state.answers.first_name) {
      state.answers.first_name = o.customerFirstNameFallback;
    }
    if (o.customerLastNameFallback && !state.answers.last_name) {
      state.answers.last_name = o.customerLastNameFallback;
    }
    if (typeof o.successRedirectDelayMs === 'number') {
      config.successRedirectDelayMs = o.successRedirectDelayMs;
    }
    if (o.clinicianBookingUrl) config.clinicianBookingUrl = o.clinicianBookingUrl;
  }

  function storageKey() {
    return (config && config.storageKey) || 'webmed_wl_assessment';
  }

  function sanitizeUploads(uploads) {
    var out = {};
    if (!uploads || typeof uploads !== 'object') return out;
    Object.keys(uploads).forEach(function (key) {
      var item = uploads[key];
      if (!item || typeof item !== 'object') return;
      var cdnUrl = item.cdnUrl || '';
      if (!cdnUrl || /^blob:|^data:/i.test(cdnUrl)) return;
      out[key] = {
        name: item.name || 'Uploaded file',
        cdnUrl: cdnUrl,
        previewUrl: cdnUrl,
        uuid: item.uuid || '',
        isPdf: !!item.isPdf
      };
    });
    return out;
  }

  function save() {
    try {
      localStorage.setItem(
        storageKey(),
        JSON.stringify({
          pageIndex: state.pageIndex,
          answers: state.answers || {},
          history: state.history || [],
          uploads: sanitizeUploads(state.uploads),
          savedAt: Date.now()
        })
      );
    } catch (e) {
      try {
        localStorage.setItem(
          storageKey(),
          JSON.stringify({
            pageIndex: state.pageIndex,
            answers: state.answers || {},
            history: state.history || [],
            uploads: {},
            savedAt: Date.now()
          })
        );
      } catch (e2) {}
    }
  }

  function clearStorage() {
    try {
      localStorage.removeItem(storageKey());
      localStorage.removeItem(storageKey() + '_result');
    } catch (e) {}
  }

  function load() {
    try {
      var raw = localStorage.getItem(storageKey());
      if (!raw) return;
      var parsed = JSON.parse(raw);
      var ttl = (config && config.storageTtlMs) || 20 * 60 * 1000;
      if (!parsed || !parsed.savedAt || Date.now() - parsed.savedAt > ttl) {
        clearStorage();
        return;
      }
      state.pageIndex = parsed.pageIndex || 0;
      state.answers = parsed.answers || {};
      state.history = parsed.history || [];
      state.uploads = sanitizeUploads(parsed.uploads);
    } catch (e) {
      clearStorage();
    }
  }

  function getPage() {
    return config.phases[state.pageIndex];
  }

  function findPhaseIndex(id) {
    for (var i = 0; i < config.phases.length; i++) {
      if (config.phases[i].id === id) return i;
    }
    return -1;
  }

  function matchesShowIf(rule) {
    if (!rule) return true;
    if (Array.isArray(rule)) {
      for (var i = 0; i < rule.length; i++) {
        if (!matchesShowIf(rule[i])) return false;
      }
      return true;
    }
    if (Array.isArray(rule.or)) {
      for (var j = 0; j < rule.or.length; j++) {
        if (matchesShowIf(rule.or[j])) return true;
      }
      return false;
    }
    var val = state.answers[rule.field];
    if (Object.prototype.hasOwnProperty.call(rule, 'equals')) {
      return val === rule.equals;
    }
    if (Object.prototype.hasOwnProperty.call(rule, 'notEquals')) {
      return val !== rule.notEquals;
    }
    if (Array.isArray(rule.in)) {
      return rule.in.indexOf(val) !== -1;
    }
    if (Object.prototype.hasOwnProperty.call(rule, 'includes')) {
      if (Array.isArray(val)) return val.indexOf(rule.includes) !== -1;
      return val === rule.includes;
    }
    return true;
  }

  function phaseVisible(page) {
    if (!page) return false;
    if (page.type === 'reject') return false;
    if (page.routeOnly) return false;
    return matchesShowIf(page.showIf);
  }

  function nextVisibleIndex(from) {
    for (var i = from + 1; i < config.phases.length; i++) {
      if (phaseVisible(config.phases[i])) return i;
    }
    return -1;
  }

  function phaseNeedsClinicianReview(phaseId) {
    var phase = null;
    var phases = (config && config.phases) || [];
    for (var i = 0; i < phases.length; i++) {
      if (phases[i].id === phaseId) {
        phase = phases[i];
        break;
      }
    }
    if (!phase || !phase.field) return false;
    var vals = state.answers[phase.field];
    if (!Array.isArray(vals)) vals = vals ? [vals] : [];
    var opts = phase.options || [];
    return vals.some(function (v) {
      if (v === 'none') return false;
      for (var j = 0; j < opts.length; j++) {
        if (opts[j].value === v && opts[j].clinicianReview) return true;
      }
      return false;
    });
  }

  function refreshClinicianReviewFlag() {
    state.answers._needs_clinician_review =
      phaseNeedsClinicianReview('q7-conditions') || phaseNeedsClinicianReview('q7a-meds');
  }

  function buildMedInfoQueue(page) {
    var queue = [];
    var seen = {};
    if (!page || !page.options) return queue;
    var selected = state.answers[page.field];
    if (!Array.isArray(selected)) selected = selected ? [selected] : [];
    // Only currently selected meds — ignore previously selected/deselected options
    selected = selected.filter(Boolean);
    page.options.forEach(function (opt) {
      if (!opt || !opt.triggerInfo) return;
      if (selected.indexOf(opt.value) === -1) return;
      if (seen[opt.triggerInfo]) return;
      seen[opt.triggerInfo] = true;
      queue.push(opt.triggerInfo);
    });
    return queue;
  }

  function getMedsPhase() {
    var idx = findPhaseIndex('q7a-meds');
    return idx >= 0 ? config.phases[idx] : null;
  }

  /** Next med-info phase still applicable to current selection (after currentPage, if any). */
  function nextMedInfoIndex(currentPage) {
    var applicable = buildMedInfoQueue(getMedsPhase());
    if (!applicable.length) return -1;
    var start = 0;
    if (currentPage && currentPage.id) {
      var at = applicable.indexOf(currentPage.id);
      start = at >= 0 ? at + 1 : 0;
    }
    for (var i = start; i < applicable.length; i++) {
      var idx = findPhaseIndex(applicable[i]);
      if (idx >= 0) return idx;
    }
    return -1;
  }

  function goTo(index) {
    if (index < 0 || index >= config.phases.length) return;
    if (window.WmAssessmentUploads && window.WmAssessmentUploads.stopCamera) {
      window.WmAssessmentUploads.stopCamera();
    }
    state.cameraActive = false;
    state.cameraFailed = false;
    state.pageIndex = index;
    state.pageError = '';
    state.uploadError = '';
    state.submitError = '';
    save();
    render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goNext() {
    var page = getPage();
    if (!pageValid(page)) {
      state.pageError = validationMessage(page) || 'Please complete this step to continue.';
      render();
      return;
    }
    var rejectPhaseId = getRejectPhaseId(page);
    if (rejectPhaseId && goToRejectPhase(rejectPhaseId)) {
      return;
    }
    var reject = getReject(page);
    if (reject) {
      state.pageError = reject;
      render();
      return;
    }

    // Option-level routing (e.g. pregnancy → Rejected screens)
    var routed = optionNextPhaseId(page);
    if (routed) {
      var routeIdx = findPhaseIndex(routed);
      if (routeIdx >= 0) {
        state.history.push(state.pageIndex);
        goTo(routeIdx);
        return;
      }
    }

    // Page-level routing (e.g. contraception method → medication history)
    if (page.nextPhaseId) {
      var pageRouteIdx = findPhaseIndex(page.nextPhaseId);
      if (pageRouteIdx >= 0) {
        state.history.push(state.pageIndex);
        goTo(pageRouteIdx);
        return;
      }
    }

    // CustomerLabs: first details step completed → wl_start_assessment
    if (page.id === 'q1-details' && window.WmClEvents) {
      window.WmClEvents.track(
        'wl_start_assessment',
        Object.assign({ funnel: 'wl' }, window.WmClEvents.identityFromAnswers(state.answers))
      );
    }

    // Final dose calendar annotations (Figma 445:7556)
    if (page.type === 'calendar') {
      applyFinalDoseDateRules(page);
    }

    // Clinician review (Figma annotations on conditions + medications)
    if (page.id === 'q7-conditions' || page.id === 'q7a-meds') {
      refreshClinicianReviewFlag();
    }

    // Medication info screens (Figma 445:7912 / 7925 / 7938 / 7951)
    // Always derive from CURRENT selection — never reuse a stale queue.
    if (page.id === 'q7a-meds') {
      state._infoQueue = null;
      var firstMedInfoIdx = nextMedInfoIndex(null);
      if (firstMedInfoIdx >= 0) {
        state.history.push(state.pageIndex);
        goTo(firstMedInfoIdx);
        return;
      }
    }

    if (page.type === 'infoAck' && page.routeOnly) {
      state._infoQueue = null;
      var nextMedInfoIdx = nextMedInfoIndex(page);
      if (nextMedInfoIdx >= 0) {
        state.history.push(state.pageIndex);
        goTo(nextMedInfoIdx);
        return;
      }
      /* Finished med-info confirms → mental health / remaining questions */
      state.history.push(state.pageIndex);
      var afterInfos = nextVisibleIndex(state.pageIndex);
      if (afterInfos !== -1) {
        goTo(afterInfos);
        return;
      }
    }

    // Out-of-range height/weight confirmations
    if (page.type === 'height' && needsHeightConfirm()) {
      openRangeModal('height');
      return;
    }
    if (page.type === 'weight') {
      if (needsWeightConfirm()) {
        openRangeModal('weight');
        return;
      }
      // Figma annotation: ineligible if BMI < 23
      if (bmiBelowMin()) {
        if (goToRejectPhase(page.rejectBmiPhaseId || 'reject-bmi')) return;
        state.pageError =
          'Based on your height and weight, your BMI is below 23 and you may not be eligible for online treatment. Please speak with your GP.';
        render();
        return;
      }
    }

    state.history.push(state.pageIndex);
    var next = nextVisibleIndex(state.pageIndex);
    if (next === -1) return;
    goTo(next);
  }

  function goBack() {
    if (!state.history.length) return;
    var prev = state.history.pop();
    state.pageError = '';
    goTo(prev);
  }

  function animateModalClose(onDone) {
    var modal = root && root.querySelector('.wma__modal');
    var reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!modal || reduce || modal.classList.contains('is-closing')) {
      if (onDone) onDone();
      return;
    }
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      modal.removeEventListener('animationend', onAnimEnd);
      if (onDone) onDone();
    }
    function onAnimEnd(e) {
      if (!e || !e.target) return;
      if (
        e.target.classList.contains('wma__modal-backdrop') ||
        e.target.classList.contains('wma__modal-stack') ||
        e.target.classList.contains('wma__modal-panel')
      ) {
        finish();
      }
    }
    modal.classList.add('is-closing');
    modal.addEventListener('animationend', onAnimEnd);
    setTimeout(finish, 280);
  }

  function closeGuidelinesModal() {
    animateModalClose(function () {
      state.guidelinesModal = null;
      render();
    });
  }

  function closeRangeModal(onDone) {
    animateModalClose(function () {
      if (onDone) onDone();
    });
  }

  function optionNextPhaseId(page) {
    if (!page || !page.field || !page.options) return '';
    var selected = state.answers[page.field];
    if (selected == null || selected === '') return '';
    for (var i = 0; i < page.options.length; i++) {
      var opt = page.options[i];
      if (opt.value === selected && opt.nextPhaseId) return opt.nextPhaseId;
    }
    return '';
  }

  function syncRxFieldsFromIso(iso) {
    var parts = String(iso || '').split('-');
    if (parts.length !== 3) return;
    state.answers.rx_yyyy = parts[0];
    state.answers.rx_mm = parts[1];
    state.answers.rx_dd = parts[2];
  }

  function applyFinalDoseDateRules(page) {
    var iso = state.answers[page.field];
    if (!iso) return;
    syncRxFieldsFromIso(iso);
    var picked = new Date(iso + 'T12:00:00');
    if (isNaN(picked.getTime())) return;
    var now = new Date();
    var diffMs = now.getTime() - picked.getTime();
    var diffWeeks = diffMs / (1000 * 60 * 60 * 24 * 7);
    var diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
    var med = state.answers.medications_to_aid_in_weight_loss || '';

    var restart = page.restartLowestDose || {};
    var rule = restart[med];
    if (rule && diffWeeks > rule.weeks) {
      state.answers.last_dose_strength = rule.dose;
      state.answers._restart_lowest_dose = true;
    } else {
      state.answers._restart_lowest_dose = false;
    }

    var months = page.skipUploadIfOlderThanMonths;
    if (months != null && diffMonths > months) {
      state.answers._skip_rx_upload = true;
    } else {
      state.answers._skip_rx_upload = false;
    }
  }

  function needsHeightConfirm() {
    var lim = config.limits || {};
    var cm = Number(state.answers.height_cm);
    if (!cm) return false;
    return cm < (lim.heightMinCm || 120) || cm > (lim.heightMaxCm || 220);
  }

  function needsWeightConfirm() {
    var lim = config.limits || {};
    var kg = Number(state.answers.weight_kg);
    if (!kg) return false;
    return kg < (lim.weightMinKg || 45) || kg > (lim.weightMaxKg || 150);
  }

  function openRangeModal(kind) {
    var lim = config.limits || {};
    if (kind === 'height') {
      var cm = Number(state.answers.height_cm);
      state.rangeModal = {
        kind: 'height',
        kindLabel: 'Height:',
        primary: cm + 'cm',
        secondary: '(' + cmToFtIn(cm) + ')',
        title: 'Before we continue, please confirm your height',
        info:
          'This height is outside the typical adult range and may affect your online assessment.'
      };
    } else {
      var kg = Number(state.answers.weight_kg);
      state.rangeModal = {
        kind: 'weight',
        kindLabel: 'Weight:',
        primary: kg + 'kg',
        secondary: '(' + kgToStLb(kg) + ')',
        title: 'Before we continue, please confirm your weight',
        info:
          'This weight is outside the typical adult range and may affect your online assessment.'
      };
    }
    render();
  }

  function cmToFtIn(cm) {
    var totalIn = cm / 2.54;
    var ft = Math.floor(totalIn / 12);
    var inches = Math.round(totalIn % 12);
    if (inches === 12) {
      ft += 1;
      inches = 0;
    }
    return ft + ' ft ' + inches + ' in';
  }

  function cmToFtInCompact(cm) {
    var totalIn = cm / 2.54;
    var ft = Math.floor(totalIn / 12);
    var inches = Math.round(totalIn % 12);
    if (inches === 12) {
      ft += 1;
      inches = 0;
    }
    return ft + 'ft ' + inches + 'in';
  }

  function cmToFtInCompactHtml(cm) {
    var totalIn = cm / 2.54;
    var ft = Math.floor(totalIn / 12);
    var inches = Math.round(totalIn % 12);
    if (inches === 12) {
      ft += 1;
      inches = 0;
    }
    return (
      '<span class="wma__ruler-sec-num">' +
      ft +
      '</span><span class="wma__ruler-sec-unit">ft </span><span class="wma__ruler-sec-num">' +
      inches +
      '</span><span class="wma__ruler-sec-unit">in</span>'
    );
  }

  function cmToFtInWords(cm) {
    var totalIn = cm / 2.54;
    var ft = Math.floor(totalIn / 12);
    var inches = Math.round(totalIn % 12);
    if (inches === 12) {
      ft += 1;
      inches = 0;
    }
    return ft + ' feet ' + inches + ' inch';
  }

  function kgToStLb(kg) {
    var totalLb = kg * 2.20462;
    var st = Math.floor(totalLb / 14);
    var lb = Math.round(totalLb % 14);
    if (lb === 14) {
      st += 1;
      lb = 0;
    }
    return st + ' st ' + lb + ' lb';
  }

  function kgToStLbCompact(kg) {
    var totalLb = kg * 2.20462;
    var st = Math.floor(totalLb / 14);
    var lb = Math.round(totalLb % 14);
    if (lb === 14) {
      st += 1;
      lb = 0;
    }
    return st + 'st ' + lb + 'lb';
  }

  function kgToStLbCompactHtml(kg) {
    var totalLb = kg * 2.20462;
    var st = Math.floor(totalLb / 14);
    var lb = Math.round(totalLb % 14);
    if (lb === 14) {
      st += 1;
      lb = 0;
    }
    return (
      '<span class="wma__ruler-sec-num">' +
      st +
      '</span><span class="wma__ruler-sec-unit">st </span><span class="wma__ruler-sec-num">' +
      lb +
      '</span><span class="wma__ruler-sec-unit">lb</span>'
    );
  }

  function calcBmi() {
    var cm = Number(state.answers.height_cm);
    var kg = Number(state.answers.weight_kg);
    if (!cm || !kg) return null;
    var m = cm / 100;
    return kg / (m * m);
  }

  function bmiBelowMin() {
    var bmi = calcBmi();
    return bmi !== null && bmi < 23;
  }

  function heightNudgeText(cm) {
    return 'Most adults are 120–220 cm. This is ' + cmToFtInWords(cm);
  }

  function weightNudgeText(kg) {
    return 'Most adults are 45–150 kg. This is ' + kgToStLb(kg);
  }

  function isHeightOutOfTypicalRange(cm) {
    var lim = config.limits || {};
    var n = Number(cm);
    if (!n) return false;
    return n < (lim.heightMinCm || 120) || n > (lim.heightMaxCm || 220);
  }

  function isWeightOutOfTypicalRange(kg) {
    var lim = config.limits || {};
    var n = Number(kg);
    if (!n) return false;
    return n < (lim.weightMinKg || 45) || n > (lim.weightMaxKg || 150);
  }

  function ensureHwDefaults(page) {
    if (page.type === 'height' && !Number(state.answers.height_cm)) {
      state.answers.height_cm = '170';
      state.answers.height_unit = 'cm';
    }
    if (page.type === 'weight' && !Number(state.answers.weight_kg)) {
      state.answers.weight_kg = '80';
      state.answers.weight_unit = 'kg';
    }
  }

  function getReject(page) {
    if (!page) return '';
    if (page.type === 'dob' && !page.fieldPrefix) {
      var age = calcAge();
      var lim = config.limits || {};
      if (age !== null && age < (lim.ageMin || 18)) {
        return page.rejectUnder18 || 'Unfortunately we cannot prescribe weight-loss treatment for patients under 18.';
      }
      if (age !== null && age >= (lim.ageMax || 75)) {
        return page.rejectOver75 || 'For patients over 75 we recommend a clinician-led review.';
      }
    }
    if (page.rejectValue && state.answers[page.field] === page.rejectValue) {
      return page.rejectMessage || '';
    }
    return '';
  }

  /** Prefer dedicated reject screens (Lotus-style eligibility stop). */
  function getRejectPhaseId(page) {
    if (!page) return '';
    if (page.type === 'dob' && !page.fieldPrefix) {
      var age = calcAge();
      var lim = config.limits || {};
      if (age !== null && age < (lim.ageMin || 18)) {
        return page.rejectUnder18PhaseId || 'reject-under-18';
      }
      if (age !== null && age >= (lim.ageMax || 75)) {
        return page.rejectOver75PhaseId || 'cl-booking-over-75';
      }
    }
    if (page.rejectPhaseId && page.rejectValue && state.answers[page.field] === page.rejectValue) {
      return page.rejectPhaseId;
    }
    return '';
  }

  function goToRejectPhase(phaseId) {
    var idx = findPhaseIndex(phaseId);
    if (idx < 0) return false;
    state.history.push(state.pageIndex);
    goTo(idx);
    return true;
  }

  /** Live DOB warning under fields — invalid calendar/future dates only (not age gates). */
  function dobWarning(page) {
    if (!page || page.type !== 'dob' || page.fieldPrefix) return '';
    var dd = String(state.answers.dob_dd || '').trim();
    var mm = String(state.answers.dob_mm || '').trim();
    var yyyy = String(state.answers.dob_yyyy || '').trim();
    if (!dd || !mm || yyyy.length < 4) return '';
    if (!isValidDate(dd, mm, yyyy)) {
      return 'Please enter a valid date of birth.';
    }
    var birth = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    if (birth > today) {
      return 'Please enter a valid date of birth.';
    }
    return '';
  }

  function calcAge() {
    var d = Number(state.answers.dob_dd);
    var m = Number(state.answers.dob_mm);
    var y = Number(state.answers.dob_yyyy);
    if (!d || !m || !y) return null;
    var birth = new Date(y, m - 1, d);
    if (birth.getFullYear() !== y || birth.getMonth() !== m - 1 || birth.getDate() !== d) {
      return null;
    }
    var now = new Date();
    var age = now.getFullYear() - y;
    if (now.getMonth() < m - 1 || (now.getMonth() === m - 1 && now.getDate() < d)) age--;
    return age;
  }

  function pageValid(page) {
    if (!page) return false;
    var a = state.answers;
    switch (page.type) {
      case 'email':
        return isValidEmail(a.email);
      case 'details':
        return (
          !!(a.first_name || '').trim() &&
          !!(a.last_name || '').trim() &&
          !!(a.phone || '').trim() &&
          isValidEmail(a.email)
        );
      case 'gpDetails':
        return (
          !!(a.gp_name || '').trim() &&
          !!(a.gp_address || '').trim() &&
          !!(a.gp_postcode || '').trim()
        );
      case 'multiselect': {
        var arr = a[page.field];
        if (!Array.isArray(arr) || !arr.length) return !!page.allowEmpty;
        if (page.otherValue && arr.indexOf(page.otherValue) !== -1) {
          return !!(a[page.field + '_other'] || '').trim();
        }
        return true;
      }
      case 'single': {
        if (!a[page.field]) return false;
        var otherTriggers = Array.isArray(page.otherValues)
          ? page.otherValues
          : page.otherValue
            ? [page.otherValue]
            : [];
        if (otherTriggers.indexOf(a[page.field]) !== -1) {
          return !!(a[page.field + '_other'] || '').trim();
        }
        return true;
      }
      case 'dob': {
        var p = page.fieldPrefix || 'dob';
        return isValidDate(a[p + '_dd'], a[p + '_mm'], a[p + '_yyyy']);
      }
      case 'height':
        return Number(a.height_cm) > 0;
      case 'weight':
        return Number(a.weight_kg) > 0;
      case 'weightLost': {
        var statusField = page.statusField || 'weight_lost_status';
        var status = a[statusField];
        if (status === 'none' || status === 'unsure') return true;
        return Number(a[page.field || 'weight_lost_on_treatment']) > 0;
      }
      case 'upload':
        if (page.allowSkip) return true;
        return !!(state.uploads[page.uploadKey] && state.uploads[page.uploadKey].cdnUrl);
      case 'confirm':
        return !!a[page.field];
      case 'infoAck':
        return !!a[page.field];
      case 'calendar':
        return !!a[page.field];
      case 'notice':
        return true;
      default:
        return true;
    }
  }

  function validationMessage(page) {
    if (!page) return '';
    if (page.type === 'email') return 'Please enter a valid email address.';
    if (page.type === 'details') {
      if (!(state.answers.first_name || '').trim()) return 'Please enter your first name.';
      if (!(state.answers.last_name || '').trim()) return 'Please enter your last name.';
      if (!(state.answers.phone || '').trim()) return 'Please enter your phone number.';
      if (!isValidEmail(state.answers.email)) return 'Please enter a valid email address.';
      return 'Please complete all required fields.';
    }
    if (page.type === 'gpDetails') {
      if (!(state.answers.gp_name || '').trim()) return 'Please enter your GP practice name.';
      if (!(state.answers.gp_address || '').trim()) return 'Please enter your GP practice address.';
      if (!(state.answers.gp_postcode || '').trim()) return 'Please enter your GP postcode.';
      return 'Please complete all required fields.';
    }
    if (page.type === 'upload' && !page.allowSkip) return 'Please upload a file to continue.';
    if (page.type === 'confirm' || page.type === 'infoAck') {
      return 'Please confirm before continuing.';
    }
    return 'Please complete this step to continue.';
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
  }

  function isValidDate(dd, mm, yyyy) {
    var d = Number(dd);
    var m = Number(mm);
    var y = Number(yyyy);
    if (!d || !m || !y || y < 1900 || y > 2100) return false;
    var dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
  }

  function resolveDoseOptions(page) {
    var med = state.answers.medications_to_aid_in_weight_loss;
    var doses = (page.dosesByMed && page.dosesByMed[med]) || [];
    return doses.map(function (d) {
      return { label: d, value: d };
    });
  }

  /* ——— Renderers ——— */

  function renderPageBody(page) {
    var u = UI();
    var a = state.answers;
    var html = '';

    if (page.type === 'email') {
      html += u.textField({
        id: 'email',
        type: 'email',
        label: page.heading,
        placeholder: page.placeholder,
        value: a.email || '',
        autocomplete: 'email'
      });
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel, 'next', { disabled: !pageValid(page) });
      return html;
    }

    if (page.type === 'details') {
      html += u.heading(page.heading || 'Please enter your details');
      html += u.detailsFields(a);
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page)
      });
      return html;
    }

    if (page.type === 'gpDetails') {
      html += u.heading(page.heading || 'Enter you GP Details');
      if (page.body) html += u.hintGp(page.body);
      html += u.gpDetailsFields(a);
      html += state.submitError ? u.alert(state.submitError) : '';
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(
        page.nextLabel || (page.isFinal ? 'View my treatment options' : 'Continue'),
        page.isFinal ? 'submit' : 'next',
        {
          disabled: !pageValid(page) || (page.isFinal && (state.submitting || !!state.submitSuccess)),
          loading: !!(page.isFinal && state.submitting)
        }
      );
      html += state.submitSuccess ? u.alert(state.submitSuccess, 'success') : '';
      return html;
    }

    if (page.type === 'multiselect' || page.type === 'single') {
      var q = Object.assign({}, page);
      if (page.id === 'q6-strength') {
        q.options = resolveDoseOptions(page);
      }
      q._otherValue = a[page.field + '_other'] || '';
      html += u.optionCards(q, a[page.field], page.type === 'multiselect');
      html += state.pageError ? u.alert(state.pageError) : '';
      // Always show Continue for single/multi (auto-advance is progressive enhancement)
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page) || !!getReject(page)
      });
      return html;
    }

    if (page.type === 'dob') {
      if (page.fieldPrefix === 'rx') {
        // legacy fallback — calendar is preferred for last dose date
        html +=
          '<fieldset class="wma__fieldset" aria-labelledby="wma-legend-rx-dob">' +
          '<div id="wma-legend-rx-dob" class="wma__legend">' +
          u.esc(page.heading) +
          '</div><div class="wma__dob-row">' +
          '<span class="wma__input-shell wma__input-shell--grow"><input class="wma__input-pill" type="text" inputmode="numeric" maxlength="2" data-field="rx_dd" placeholder="DD" value="' +
          u.esc(a.rx_dd || '') +
          '" aria-label="Day"></span>' +
          '<span class="wma__input-shell wma__input-shell--grow"><input class="wma__input-pill" type="text" inputmode="numeric" maxlength="2" data-field="rx_mm" placeholder="MM" value="' +
          u.esc(a.rx_mm || '') +
          '" aria-label="Month"></span>' +
          '<span class="wma__input-shell wma__input-shell--grow"><input class="wma__input-pill" type="text" inputmode="numeric" maxlength="4" data-field="rx_yyyy" placeholder="YYYY" value="' +
          u.esc(a.rx_yyyy || '') +
          '" aria-label="Year"></span>' +
          '</div></fieldset>';
        html += state.pageError ? u.alert(state.pageError, 'error') : '';
        html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
          disabled: !pageValid(page)
        });
      } else {
        var invalidMsg = dobWarning(page);
        var alertMsg = invalidMsg || state.pageError;
        html += u.dobFields(a, { invalid: !!invalidMsg });
        if (alertMsg) {
          html +=
            '<p class="wma__alert wma__alert--error" role="alert" data-dob-alert="1">' +
            u.esc(alertMsg) +
            '</p>';
        }
        html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
          disabled: !pageValid(page) || !!invalidMsg
        });
      }
      return html;
    }

    if (page.type === 'calendar') {
      html += u.heading(page.heading);
      html += u.calendarPicker({
        field: page.field || 'last_dose_date',
        selected: a[page.field] || '',
        viewYear: state._calViewYear,
        viewMonth: state._calViewMonth
      });
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page)
      });
      return html;
    }

    if (page.type === 'height') {
      ensureHwDefaults(page);
      var cm = Number(a.height_cm) || 170;
      html += u.heading(page.heading);
      html += u.hint(page.hint);
      html += u.rulerPicker({
        field: 'height_cm',
        value: cm,
        unit: 'cm',
        secondaryHtml: cmToFtInCompactHtml(cm),
        min: 100,
        max: 250,
        step: 1,
        ariaLabel: 'Height in centimetres',
        nudge: heightNudgeText(cm)
      });
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page)
      });
      return html;
    }

    if (page.type === 'weight') {
      ensureHwDefaults(page);
      var kg = Number(a.weight_kg) || 80;
      html += u.heading(page.heading);
      html += u.hint(page.hint);
      html += u.rulerPicker({
        field: 'weight_kg',
        value: kg,
        unit: 'kg',
        secondaryHtml: kgToStLbCompactHtml(kg),
        min: 30,
        max: 350,
        step: 1,
        ariaLabel: 'Weight in kilograms',
        nudge: weightNudgeText(kg)
      });
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page)
      });
      return html;
    }

    if (page.type === 'weightLost') {
      var lostField = page.field || 'weight_lost_on_treatment';
      var lostStatusField = page.statusField || 'weight_lost_status';
      var lostStatus = a[lostStatusField] || '';
      var lostKg = Number(a[lostField]) || 0;
      if (!lostKg) lostKg = 5;
      html += u.heading(page.heading);
      /* Figma 1604:4580 — ruler stays visible alongside choice options */
      html += u.rulerPicker({
        field: lostField,
        value: lostKg,
        unit: 'kg',
        secondaryHtml: kgToStLbCompactHtml(lostKg),
        min: 1,
        max: 150,
        step: 1,
        ariaLabel: 'Weight lost in kilograms',
        nudge: weightNudgeText(lostKg)
      });
      html += u.optionCards(
        {
          field: lostStatusField,
          options: page.choiceOptions || [],
          _otherValue: ''
        },
        lostStatus,
        false
      );
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page)
      });
      return html;
    }

    if (page.type === 'notice') {
      html += u.noticeScreen(page);
      return html;
    }

    if (page.type === 'infoAck') {
      html += u.heading(page.heading);
      html += u.infoAckBody(page);
      html += u.confirmToggle(page.field, !!a[page.field], page.confirmLabel, {
        accent: true
      });
      html += state.pageError ? u.alert(state.pageError) : '';
      html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
        disabled: !pageValid(page)
      });
      return html;
    }

    if (page.type === 'reject') {
      html += u.rejectScreen(page);
      return html;
    }

    if (page.type === 'upload') {
      return renderUpload(page);
    }

    if (page.type === 'confirm') {
      html += u.heading(page.heading);
      if ((page.bodyList && page.bodyList.length) || (page.bodyParagraphs && page.bodyParagraphs.length)) {
        html += u.infoAckBody(page);
      }
      html += u.confirmToggle(page.field, !!a[page.field], page.confirmLabel, {
        accent: true
      });
      html += state.submitError ? u.alert(state.submitError) : '';
      html += state.pageError ? u.alert(state.pageError) : '';
      /* GP details is final submit step — confirm only advances */
      html += u.primaryButton(page.nextLabel || 'Continue', page.isFinal ? 'submit' : 'next', {
        disabled: !pageValid(page) || (page.isFinal && (state.submitting || !!state.submitSuccess)),
        loading: !!(page.isFinal && state.submitting)
      });
      html += state.submitSuccess ? u.alert(state.submitSuccess, 'success') : '';
      return html;
    }

    return u.alert('Unknown step type', 'error');
  }

  function renderUpload(page) {
    var u = UI();
    var upload = state.uploads[page.uploadKey];
    var isProof = page.uploadVariant === 'proof';
    var isPhoto = page.uploadVariant === 'photo';
    var isPhotoId = page.uploadVariant === 'photoId';
    var isCameraUpload = isPhoto || isPhotoId;
    var html = u.heading(page.heading);
    if (page.bodyParagraphs || page.bodyList) {
      html += u.infoAckBody(page);
    } else if (page.hint && !isCameraUpload) {
      html += u.hint(page.hint);
    }

    if (isCameraUpload) {
      var idType = '';
      if (isPhotoId) {
        idType = state.answers[page.idTypeField || 'identity_document_type'] || 'driving_licence';
        var idHeading =
          idType === 'passport'
            ? 'Upload or take a photo of your passport'
            : idType === 'other'
              ? page.heading || 'Please Upload photo of your ID'
              : 'Upload or take a photo of your Driving Licence';
        html = u.heading(idHeading);
      }

      var hasFile =
        upload && (upload.cdnUrl || upload.uploading || upload.progress != null);
      var cameraLive = !!state.cameraActive && !hasFile;
      var previewSrc = '';
      if (upload) {
        previewSrc =
          upload.localPreviewUrl ||
          (!upload.isPdf && (upload.previewUrl || upload.cdnUrl)) ||
          '';
      }

      if (!hasFile && !cameraLive) {
        if (isPhotoId && idType !== 'other') {
          html += u.idDocExamples(root, idType);
        } else if (page.showExamples) {
          html += u.photoExamples(root);
        }
      }

      if (hasFile || cameraLive) {
        html += u.photoPreviewBox({
          camera: cameraLive,
          src: previewSrc,
          alt: (upload && upload.name) || 'Uploaded photo'
        });
        if (hasFile) {
          html += u.uploadFileRow({
            name: upload.name || 'Image',
            uploadKey: page.uploadKey,
            progress: upload.cdnUrl ? 100 : upload.progress || 0,
            uploading: !!upload.uploading && !upload.cdnUrl
          });
        }
      }

      html += state.uploadError ? u.alert(state.uploadError) : '';
      html += state.pageError ? u.alert(state.pageError) : '';
      html +=
        '<div class="wma__actions' +
        (isPhotoId && idType === 'other' && !hasFile && !cameraLive
          ? ' wma__actions--id-other'
          : '') +
        '">';
      if (hasFile || cameraLive) {
        if (isPhoto && page.requirementsLabel) {
          html +=
            '<button type="button" class="wma__btn wma__btn--link" data-action="guidelines" data-guidelines="' +
            u.esc(page.guidelinesKey || 'fullbody') +
            '">' +
            u.esc(page.requirementsLabel) +
            '</button>';
        }
        if (cameraLive) {
          html +=
            '<button type="button" class="wma__btn wm-btn wm-btn--primary wm-btn--lg" data-action="upload-camera" data-upload-key="' +
            u.esc(page.uploadKey) +
            '">' +
            u.esc(page.cameraLabel || 'Take a photo') +
            '</button>';
          html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
            disabled: true
          });
        } else {
          html += u.primaryButton(page.nextLabel || 'Continue', 'next', {
            disabled: !upload.cdnUrl
          });
        }
      } else {
        html +=
          '<button type="button" class="wma__btn wm-btn wm-btn--primary wm-btn--lg" data-action="upload-camera" data-upload-key="' +
          u.esc(page.uploadKey) +
          '">' +
          u.esc(page.cameraLabel || 'Take a photo') +
          '</button>';
        html += u.secondaryButton(page.galleryLabel || 'Upload photo', 'upload-gallery', {
          cream: true,
          uploadKey: page.uploadKey
        });
        if (page.allowSkip) {
          html += u.tertiaryButton(page.skipLabel || 'Skip for now', 'skip-upload');
        }
      }
      html += '</div>';
      return html;
    }

    if (page.showExamples && !upload) {
      html +=
        '<div class="wma__examples">' +
        exampleImg('data-guideline-fb-do', 'Do: full body') +
        exampleImg('data-guideline-fb-dont-baggy', "Don't: baggy") +
        exampleImg('data-guideline-fb-dont-face', "Don't: face only") +
        exampleImg('data-guideline-fb-dont-body', "Don't: partial") +
        '</div>';
    }

    if (isProof) {
      var previewSrcProof = '';
      if (upload) {
        if (upload.localPreviewUrl) previewSrcProof = upload.localPreviewUrl;
        else if (upload.cdnUrl && !upload.isPdf) {
          previewSrcProof = upload.previewUrl || upload.cdnUrl;
        }
      }
      html += u.uploadZone({
        variant: 'proof',
        uploadKey: page.uploadKey,
        label: page.uploadLabel,
        hint: page.uploadHint,
        previewUrl: previewSrcProof,
        previewAlt: (upload && upload.name) || 'Uploaded image preview',
        isPdf: !!(upload && upload.isPdf),
        uploading: !!(upload && upload.uploading && !upload.cdnUrl)
      });
      if (upload && (upload.cdnUrl || upload.uploading || upload.progress != null)) {
        html += u.uploadFileRow({
          name: upload.name || 'Prescription',
          uploadKey: page.uploadKey,
          progress: upload.cdnUrl ? 100 : upload.progress || 0,
          uploading: !!upload.uploading && !upload.cdnUrl
        });
      }
    } else if (upload && upload.cdnUrl) {
      html += '<div class="wma__upload-preview">';
      if (!upload.isPdf) {
        html +=
          '<img src="' +
          u.esc(upload.previewUrl || upload.cdnUrl) +
          '" alt="Uploaded file preview">';
      }
      html +=
        '<div class="wma__upload-meta"><span>' +
        u.esc(upload.name) +
        '</span><button type="button" class="wma__btn wma__btn--tertiary" data-action="clear-upload" data-upload-key="' +
        u.esc(page.uploadKey) +
        '">Remove</button></div></div>';
    } else {
      html += u.uploadZone({
        uploadKey: page.uploadKey,
        label: 'Tap to upload or take a photo',
        hint: 'PNG, JPG or PDF · max 3MB'
      });
    }

    html += state.uploadError ? u.alert(state.uploadError) : '';
    html += state.pageError ? u.alert(state.pageError) : '';

    html += '<div class="wma__actions">';
    if (isProof) {
      if (upload && upload.cdnUrl) {
        html += u.primaryButton(page.nextLabel || 'Continue', 'next');
      } else {
        html += u.primaryButton(
          upload && upload.uploading ? 'Uploading…' : page.nextLabel || 'Continue',
          'next',
          { disabled: true }
        );
      }
    } else if (upload && upload.cdnUrl) {
      html += u.primaryButton('Continue', 'next');
    } else {
      var keyAttr = ' data-upload-key="' + u.esc(page.uploadKey) + '"';
      html +=
        '<button type="button" class="wma__btn wm-btn wm-btn--primary wm-btn--lg" data-action="upload"' +
        keyAttr +
        '>' +
        u.esc(page.nextLabel || 'Upload') +
        '</button>';
      if (page.secondaryLabel) {
        html +=
          '<button type="button" class="wma__btn wma__btn--secondary" data-action="upload"' +
          keyAttr +
          '>' +
          u.esc(page.secondaryLabel) +
          '</button>';
      }
      if (page.tertiaryLabel) {
        html +=
          '<button type="button" class="wma__btn wma__btn--tertiary" data-action="guidelines" data-guidelines="' +
          u.esc(page.guidelinesKey || '') +
          '">' +
          u.esc(page.tertiaryLabel) +
          '</button>';
      }
      if (page.allowSkip) {
        html += u.tertiaryButton(page.skipLabel || 'Upload later', 'skip-upload');
      }
    }
    html += '</div>';
    return html;
  }

  function exampleImg(attr, caption) {
    var u = UI();
    var src = root.getAttribute(attr) || '';
    return (
      '<div class="wma__example">' +
      (src
        ? '<img src="' + u.esc(src) + '" alt="' + u.esc(caption) + '" width="161" height="180">'
        : '<div style="height:180px;background:#f8f8f8;border-radius:8px" aria-hidden="true"></div>') +
      '<span>' +
      u.esc(caption) +
      '</span></div>'
    );
  }

  function render() {
    if (!root || !config) return;

    if (state.submitted) {
      var successHtml = UI().shell({
        progress: 100,
        showBack: false,
        hideChrome: false,
        animate: true,
        body:
          UI().heading('Thank you') +
          '<p class="wma__body">Your assessment has been submitted successfully. Our team will be in touch shortly.</p>' +
          UI().primaryButton('Go back to Home', 'go-home')
      });
      root.innerHTML = successHtml;
      bindEvents();
      return;
    }

    // Skip invisible phases if restored mid-branch (keep reject screens when routed)
    while (state.pageIndex < config.phases.length) {
      var probe = getPage();
      if (!probe) break;
      if (probe.type === 'reject' || probe.routeOnly || phaseVisible(probe)) break;
      state.pageIndex = nextVisibleIndex(state.pageIndex - 1);
      if (state.pageIndex < 0) {
        state.pageIndex = 0;
        break;
      }
    }

    var page = getPage();
    if (!page) {
      root.innerHTML = UI().alert('Assessment configuration error', 'error');
      return;
    }

    // Animate only when navigating to a different step (not option toggles on same step)
    var animate = state._lastRenderedPageIndex !== state.pageIndex;
    state._lastRenderedPageIndex = state.pageIndex;

    var html = UI().shell({
      progress:
        page.progress != null
          ? page.progress
          : Math.round(((state.pageIndex + 1) / config.phases.length) * 100),
      showBack: !page.hideChrome && page.type !== 'reject' && !!page.showBack && state.pageIndex > 0,
      hideChrome: page.type === 'reject' || !!page.hideChrome,
      animate: animate,
      body: renderPageBody(page)
    });

    if (state.guidelinesModal) {
      html += window.WmAssessmentUploads.guidelinesHtml(state.guidelinesModal, root);
    }
    if (state.rangeModal) {
      html += window.WmAssessmentUploads.rangeConfirmModal(state.rangeModal);
    }

    root.innerHTML = html;
    if (animate) animateStepIn();
    bindEvents();
    bindGpDetails();
    bindRulers();
    bindCalendar();
    mountCameraPreview();
  }

  function animateStepIn() {
    var el = root && root.querySelector('.wma__slide-enter');
    if (!el) return;
    requestAnimationFrame(function () {
      el.classList.remove('wma__slide-enter');
      el.classList.add('wma__slide-active');
    });
  }

  function mountCameraPreview() {
    if (!state.cameraActive || state.cameraFailed) return;
    var page = getPage();
    if (!page || (page.uploadVariant !== 'photo' && page.uploadVariant !== 'photoId')) return;
    var upload = state.uploads[page.uploadKey];
    if (upload && (upload.cdnUrl || upload.uploading || upload.progress != null)) return;
    var video = root.querySelector('[data-camera-preview]');
    if (!video || !window.WmAssessmentUploads || !window.WmAssessmentUploads.startCamera) {
      return;
    }
    window.WmAssessmentUploads.startCamera(video).catch(function (err) {
      state.cameraFailed = true;
      state.uploadError =
        (err && err.message) ||
        'Unable to open camera. You can still tap Take a photo to use your device camera.';
      var alertHost = root.querySelector('.wma__actions');
      if (alertHost && !root.querySelector('.wma__alert--error')) {
        alertHost.insertAdjacentHTML(
          'beforebegin',
          UI().alert(state.uploadError)
        );
      }
    });
  }

  function syncHeightCm() {
    // kept for legacy unit fields; ruler writes height_cm directly
    var a = state.answers;
    if ((a.height_unit || 'cm') === 'cm') {
      var cm = Number(a.height_cm_input || a.height_cm);
      a.height_cm = cm > 0 ? String(Math.round(cm)) : '';
    } else {
      var ft = Number(a.height_ft) || 0;
      var inches = Number(a.height_in) || 0;
      var total = ft * 30.48 + inches * 2.54;
      a.height_cm = total > 0 ? String(Math.round(total)) : '';
    }
  }

  function syncWeightKg() {
    var a = state.answers;
    if ((a.weight_unit || 'kg') === 'kg') {
      var kg = Number(a.weight_kg_input || a.weight_kg);
      a.weight_kg = kg > 0 ? String(Math.round(kg * 10) / 10) : '';
    } else {
      var st = Number(a.weight_st) || 0;
      var lb = Number(a.weight_lb) || 0;
      var total = st * 6.35029 + lb * 0.453592;
      a.weight_kg = total > 0 ? String(Math.round(total * 10) / 10) : '';
    }
  }

  function formatGpAddressParts(org) {
    if (!org) return { address: '', postcode: '' };
    var parts = [];
    var postcode = '';
    if (org.GeoLoc && org.GeoLoc.Location) {
      var loc = org.GeoLoc.Location;
      ['AddrLn1', 'AddrLn2', 'AddrLn3', 'Town', 'County', 'Country'].forEach(function (k) {
        if (loc[k]) parts.push(loc[k]);
      });
      postcode = loc.PostCode || '';
    }
    if (!postcode && org.PostCode) postcode = org.PostCode;
    return { address: parts.join(', '), postcode: postcode };
  }

  /* NHS Organisation Data Service — same as Refound gp-details */
  function bindGpDetails() {
    var page = getPage();
    if (!page || page.type !== 'gpDetails') return;

    var surgeryInput = root.querySelector('input[data-field="gp_name"]');
    var addressInput = root.querySelector('input[data-field="gp_address"]');
    var postcodeInput = root.querySelector('input[data-field="gp_postcode"]');
    var suggestionsEl = root.querySelector('[data-gp-suggestions]');
    var gpSearchTimer = null;
    var gpRequestId = 0;
    var u = UI();

    function hideGpSuggestions() {
      if (!suggestionsEl) return;
      suggestionsEl.innerHTML = '';
      suggestionsEl.hidden = true;
    }

    function showGpSuggestions(items) {
      if (!suggestionsEl) return;
      if (!items || !items.length) {
        hideGpSuggestions();
        return;
      }
      var html = '';
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        html +=
          '<button type="button" class="wma__gp-suggestion" data-gp-name="' +
          u.esc(item.name) +
          '" data-gp-address="' +
          u.esc(item.address) +
          '" data-gp-postcode="' +
          u.esc(item.postcode || '') +
          '" data-gp-orgid="' +
          u.esc(item.orgId || '') +
          '">';
        html += '<span class="wma__gp-suggestion-name">' + u.esc(item.name) + '</span>';
        if (item.address || item.postcode) {
          html +=
            '<span class="wma__gp-suggestion-address">' +
            u.esc([item.address, item.postcode].filter(Boolean).join(', ')) +
            '</span>';
        }
        html += '</button>';
      }
      suggestionsEl.innerHTML = html;
      suggestionsEl.hidden = false;
    }

    function searchNhsGp(query) {
      var reqId = ++gpRequestId;
      var url =
        'https://directory.spineservices.nhs.uk/ORD/2-0-0/organisations?Name=' +
        encodeURIComponent(query) +
        '&Limit=8&Status=Active&PrimaryRoleId=RO177';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.ok ? res.json() : Promise.reject(res);
        })
        .then(function (data) {
          if (reqId !== gpRequestId) return;
          var orgs = data && data.Organisations ? data.Organisations : [];
          var items = [];
          for (var i = 0; i < orgs.length; i++) {
            var org = orgs[i];
            items.push({
              name: org.Name || '',
              address: org.PostCode || '',
              postcode: org.PostCode || '',
              orgId: org.OrgId || ''
            });
          }
          showGpSuggestions(items);
        })
        .catch(function () {
          if (reqId === gpRequestId) hideGpSuggestions();
        });
    }

    function applyGpSelection(name, address, postcode, orgId) {
      if (surgeryInput) {
        surgeryInput.value = name || '';
        state.answers.gp_name = surgeryInput.value;
      }
      if (addressInput && address) {
        addressInput.value = address;
        state.answers.gp_address = address;
      }
      if (postcodeInput && postcode) {
        postcodeInput.value = postcode;
        state.answers.gp_postcode = postcode;
      }
      hideGpSuggestions();
      save();
      syncPrimaryButton();
      if (!orgId) return;
      fetch(
        'https://directory.spineservices.nhs.uk/ORD/2-0-0/organisations/' +
          encodeURIComponent(orgId),
        { headers: { Accept: 'application/json' } }
      )
        .then(function (res) {
          return res.ok ? res.json() : Promise.reject(res);
        })
        .then(function (data) {
          var org = data && data.Organisation ? data.Organisation : null;
          var parts = formatGpAddressParts(org);
          if (parts.address && addressInput) {
            addressInput.value = parts.address;
            state.answers.gp_address = parts.address;
          }
          if (parts.postcode && postcodeInput) {
            postcodeInput.value = parts.postcode;
            state.answers.gp_postcode = parts.postcode;
          }
          save();
          syncPrimaryButton();
        })
        .catch(function () {});
    }

    if (surgeryInput) {
      surgeryInput.addEventListener('input', function () {
        var query = surgeryInput.value.trim();
        clearTimeout(gpSearchTimer);
        if (query.length < 3) {
          hideGpSuggestions();
          return;
        }
        gpSearchTimer = setTimeout(function () {
          searchNhsGp(query);
        }, 300);
      });
      surgeryInput.addEventListener('blur', function () {
        setTimeout(hideGpSuggestions, 150);
      });
    }

    if (suggestionsEl) {
      suggestionsEl.addEventListener('mousedown', function (e) {
        var btn = e.target.closest('[data-gp-name]');
        if (!btn) return;
        e.preventDefault();
        applyGpSelection(
          btn.getAttribute('data-gp-name') || '',
          btn.getAttribute('data-gp-address') || '',
          btn.getAttribute('data-gp-postcode') || '',
          btn.getAttribute('data-gp-orgid') || ''
        );
      });
    }
  }

  function bindCalendar() {
    var wrap = root.querySelector('[data-calendar]');
    if (!wrap) return;
    var field = wrap.getAttribute('data-field') || 'last_dose_date';

    function refreshCal() {
      var vy = parseInt(wrap.getAttribute('data-view-year'), 10);
      var vm = parseInt(wrap.getAttribute('data-view-month'), 10);
      var sel = wrap.getAttribute('data-selected') || '';
      wrap.innerHTML = UI().calendarGrid(vy, vm, sel);
      bindCalControls();
    }

    function bindCalControls() {
      var prev = wrap.querySelector('[data-action="cal-prev"]');
      var next = wrap.querySelector('[data-action="cal-next"]');
      if (prev) {
        prev.addEventListener('click', function () {
          var vy = parseInt(wrap.getAttribute('data-view-year'), 10);
          var vm = parseInt(wrap.getAttribute('data-view-month'), 10);
          vm -= 1;
          if (vm < 0) {
            vm = 11;
            vy -= 1;
          }
          wrap.setAttribute('data-view-year', String(vy));
          wrap.setAttribute('data-view-month', String(vm));
          state._calViewYear = vy;
          state._calViewMonth = vm;
          refreshCal();
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          var vy = parseInt(wrap.getAttribute('data-view-year'), 10);
          var vm = parseInt(wrap.getAttribute('data-view-month'), 10);
          vm += 1;
          if (vm > 11) {
            vm = 0;
            vy += 1;
          }
          wrap.setAttribute('data-view-year', String(vy));
          wrap.setAttribute('data-view-month', String(vm));
          state._calViewYear = vy;
          state._calViewMonth = vm;
          refreshCal();
        });
      }
      wrap.querySelectorAll('[data-action="cal-select"]').forEach(function (cell) {
        cell.addEventListener('click', function () {
          var iso = cell.getAttribute('data-date');
          if (!iso) return;
          wrap.setAttribute('data-selected', iso);
          state.answers[field] = iso;
          syncRxFieldsFromIso(iso);
          state.pageError = '';
          save();
          wrap.querySelectorAll('.wma__cal-day.is-current').forEach(function (d) {
            d.classList.remove('is-selected');
            d.setAttribute('aria-pressed', 'false');
          });
          cell.classList.add('is-selected');
          cell.setAttribute('aria-pressed', 'true');
          syncPrimaryButton();
        });
      });
    }

    bindCalControls();
  }

  function bindRulers() {
    root.querySelectorAll('[data-ruler-root]').forEach(function (wrap) {
      var input = wrap.querySelector('.wma__ruler-input');
      var track = wrap.querySelector('[data-ruler-track]');
      var viewport = wrap.querySelector('.wma__ruler-viewport');
      if (!input || !track || !viewport) return;

      var min = Number(input.min);
      var max = Number(input.max);
      var step = Number(input.step) || 1;
      var field = input.getAttribute('data-field');
      var tickW = 19;
      var count = Math.floor((max - min) / step) + 1;
      // Extra visual ticks beyond min/max so both sides stay filled at the ends
      var lead = 16;
      var trail = 16;
      var totalTicks = lead + count + trail;

      var ticksHtml = '';
      var i;
      for (i = 0; i < totalTicks; i++) {
        var v = min + (i - lead) * step;
        var major = ((v % 5) + 5) % 5 === 0;
        var inRange = i >= lead && i < lead + count;
        ticksHtml +=
          '<span class="wma__ruler-tick' +
          (major ? ' is-major' : '') +
          (inRange ? '' : ' is-pad') +
          '" data-v="' +
          v +
          '"></span>';
      }
      track.innerHTML = ticksHtml;
      track.style.width = totalTicks * tickW + 'px';
      track.style.paddingLeft = '0';
      track.style.paddingRight = '0';

      function scrollToValue(val, smooth) {
        val = Math.max(min, Math.min(max, val));
        var idx = lead + Math.round((val - min) / step);
        var x = idx * tickW;
        viewport.scrollTo({ left: Math.max(0, x), behavior: smooth ? 'smooth' : 'auto' });
      }

      function valueFromScroll() {
        var idx = Math.round(viewport.scrollLeft / tickW);
        if (idx < lead) return min;
        if (idx >= lead + count) return max;
        return min + (idx - lead) * step;
      }

      function applyValue(val, fromScroll, isInit) {
        val = Math.max(min, Math.min(max, Math.round(val / step) * step));
        input.value = String(val);
        state.answers[field] = String(val);
        if (field === 'height_cm') state.answers.height_unit = 'cm';
        if (field === 'weight_kg') state.answers.weight_unit = 'kg';
        /* Only clear status on real user ruler interaction — not init / programmatic snap */
        if (
          !isInit &&
          !ignoreProgrammaticScroll &&
          (field === 'weight_lost_kg' || field === 'weight_lost_on_treatment')
        ) {
          state.answers.weight_lost_status = '';
        }
        updateRulerReadout(wrap, field, val);
        if (!fromScroll) scrollToValue(val, false);
        state.pageError = '';
        save();
        syncPrimaryButton();
      }

      var scrolling = false;
      var scrollTimer = null;
      var ignoreProgrammaticScroll = true;
      viewport.addEventListener(
        'scroll',
        function () {
          if (ignoreProgrammaticScroll) return;
          scrolling = true;
          var val = valueFromScroll();
          input.value = String(val);
          state.answers[field] = String(val);
          if (
            field === 'weight_lost_kg' ||
            field === 'weight_lost_on_treatment'
          ) {
            state.answers.weight_lost_status = '';
          }
          updateRulerReadout(wrap, field, val);
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(function () {
            scrolling = false;
            applyValue(val, true);
            // Snap back onto the selectable range (stops at min/max)
            scrollToValue(val, true);
          }, 80);
        },
        { passive: true }
      );

      input.addEventListener('input', function () {
        if (scrolling) return;
        applyValue(Number(input.value), false);
      });

      var dragging = false;
      var startX = 0;
      var startScroll = 0;
      viewport.addEventListener('pointerdown', function (e) {
        ignoreProgrammaticScroll = false;
        dragging = true;
        startX = e.clientX;
        startScroll = viewport.scrollLeft;
        try {
          viewport.setPointerCapture(e.pointerId);
        } catch (err) {}
      });
      viewport.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        viewport.scrollLeft = startScroll - (e.clientX - startX);
      });
      viewport.addEventListener('pointerup', function () {
        dragging = false;
      });
      viewport.addEventListener('pointercancel', function () {
        dragging = false;
      });

      requestAnimationFrame(function () {
        applyValue(Number(input.value) || min, false, true);
        /* Unlock after snap settles; pointerdown also unlocks immediately */
        setTimeout(function () {
          ignoreProgrammaticScroll = false;
        }, 250);
      });
    });
  }

  function updateRulerReadout(wrap, field, val) {
    var valueEl = wrap.querySelector('[data-ruler-value]');
    var secondaryEl = wrap.querySelector('[data-ruler-secondary]');
    var nudge = wrap.querySelector('[data-ruler-nudge]');
    var nudgeText = wrap.querySelector('[data-ruler-nudge-text]');
    var labels = wrap.querySelector('[data-ruler-labels]');
    if (valueEl) valueEl.textContent = String(Math.round(val));
    if (secondaryEl) {
      if (field === 'height_cm') {
        secondaryEl.innerHTML = cmToFtInCompactHtml(val);
      } else {
        secondaryEl.innerHTML = kgToStLbCompactHtml(val);
      }
    }
    var msg =
      field === 'height_cm' ? heightNudgeText(val) : weightNudgeText(val);
    if (nudge && nudgeText) {
      nudgeText.textContent = msg;
      nudge.hidden = false;
      nudge.classList.remove('is-hidden');
      nudge.removeAttribute('hidden');
    }
    if (labels) {
      var left = Math.round(val) - 5;
      var mid = Math.round(val);
      var right = Math.round(val) + 5;
      labels.innerHTML =
        '<span>' +
        left +
        '</span><span>' +
        mid +
        '</span><span>' +
        right +
        '</span>';
    }
  }

  function bindEvents() {
    root.querySelectorAll('[data-action="back"]').forEach(function (el) {
      el.addEventListener('click', goBack);
    });
    root.querySelectorAll('[data-action="next"]').forEach(function (el) {
      el.addEventListener('click', function () {
        if (el.classList.contains('is-disabled') || el.disabled) return;
        goNext();
      });
    });
    root.querySelectorAll('[data-action="go-home"]').forEach(function (el) {
      el.addEventListener('click', function () {
        clearStorage();
        window.location.href = (config && config.homeUrl) || '/';
      });
    });
    root.querySelectorAll('[data-action="book-clinician"]').forEach(function (el) {
      el.addEventListener('click', function () {
        clearStorage();
        window.location.href =
          (config && (config.clinicianBookingUrl || config.homeUrl)) || '/';
      });
    });
    root.querySelectorAll('[data-action="skip-upload"]').forEach(function (el) {
      el.addEventListener('click', goNext);
    });
    root.querySelectorAll('[data-action="submit"]').forEach(function (el) {
      el.addEventListener('click', function () {
        if (el.disabled || state.submitting) return;
        doSubmit();
      });
    });
    root.querySelectorAll('[data-action="upload"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var key = el.getAttribute('data-upload-key') || (getPage() && getPage().uploadKey);
        if (!key) return;
        state.uploadError = '';
        window.WmAssessmentUploads.openUploader(key, api);
      });
    });
    root.querySelectorAll('[data-action="upload-camera"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var key = el.getAttribute('data-upload-key') || (getPage() && getPage().uploadKey);
        if (!key) return;
        state.uploadError = '';
        var upload = state.uploads[key];
        var hasFile =
          upload && (upload.cdnUrl || upload.uploading || upload.progress != null);

        // Live camera open — capture frame into upload (or native fallback)
        if (state.cameraActive && !hasFile) {
          var video = root.querySelector('[data-camera-preview]');
          var canCapture =
            video &&
            video.videoWidth > 0 &&
            window.WmAssessmentUploads.captureCameraFile &&
            !state.cameraFailed;
          if (!canCapture) {
            window.WmAssessmentUploads.openUploader(key, api, {
              capture: true,
              forcePicker: true
            });
            return;
          }
          window.WmAssessmentUploads.captureCameraFile(video)
            .then(function (file) {
              state.cameraActive = false;
              state.cameraFailed = false;
              window.WmAssessmentUploads.stopCamera();
              window.WmAssessmentUploads.uploadFile(file, key, api);
            })
            .catch(function (err) {
              state.uploadError =
                (err && err.message) || 'Unable to capture photo. Please try again.';
              render();
            });
          return;
        }

        // Retake or open in-page camera preview (Figma 445:7382)
        if (hasFile) {
          if (window.WmAssessmentUploads.abortUpload) {
            window.WmAssessmentUploads.abortUpload(key);
          }
          if (window.WmAssessmentUploads.revokeLocalPreview) {
            window.WmAssessmentUploads.revokeLocalPreview(key);
          }
          delete state.uploads[key];
          save();
        }
        state.cameraActive = true;
        state.cameraFailed = false;
        state.uploadError = '';
        render();
      });
    });
    root.querySelectorAll('[data-action="upload-gallery"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var key = el.getAttribute('data-upload-key') || (getPage() && getPage().uploadKey);
        if (!key) return;
        state.uploadError = '';
        state.cameraActive = false;
        state.cameraFailed = false;
        if (window.WmAssessmentUploads.stopCamera) {
          window.WmAssessmentUploads.stopCamera();
        }
        window.WmAssessmentUploads.openUploader(key, api, {
          capture: false,
          forcePicker: true
        });
      });
    });
    root.querySelectorAll('[data-upload-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        var key = input.getAttribute('data-upload-key') || (getPage() && getPage().uploadKey);
        var file = input.files && input.files[0];
        input.value = '';
        if (!key || !file) return;
        state.uploadError = '';
        window.WmAssessmentUploads.uploadFile(file, key, api);
      });
    });
    root.querySelectorAll('[data-action="clear-upload"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var key = el.getAttribute('data-upload-key');
        if (key) {
          if (window.WmAssessmentUploads && window.WmAssessmentUploads.abortUpload) {
            window.WmAssessmentUploads.abortUpload(key);
          }
          if (window.WmAssessmentUploads && window.WmAssessmentUploads.revokeLocalPreview) {
            window.WmAssessmentUploads.revokeLocalPreview(key);
          }
          if (window.WmAssessmentUploads && window.WmAssessmentUploads.stopCamera) {
            window.WmAssessmentUploads.stopCamera();
          }
          delete state.uploads[key];
          var page = getPage();
          var reopenCamera =
            page &&
            (page.uploadVariant === 'photo' || page.uploadVariant === 'photoId') &&
            page.uploadKey === key;
          state.cameraActive = !!reopenCamera;
          state.cameraFailed = false;
          state.uploadError = '';
          save();
          render();
        }
      });
    });
    root.querySelectorAll('[data-action="guidelines"]').forEach(function (el) {
      el.addEventListener('click', function () {
        state.guidelinesModal = el.getAttribute('data-guidelines') || 'fullbody';
        render();
      });
    });
    root.querySelectorAll('[data-action="close-guidelines"]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeGuidelinesModal();
      });
    });
    root.querySelectorAll('[data-action="close-range"]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeRangeModal(function () {
          state.rangeModal = null;
          render();
        });
      });
    });
    root.querySelectorAll('[data-action="confirm-range"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var page = getPage();
        closeRangeModal(function () {
          state.rangeModal = null;
          // After confirming an out-of-range weight, still gate on BMI < 23
          if (page && page.type === 'weight' && bmiBelowMin()) {
            if (goToRejectPhase(page.rejectBmiPhaseId || 'reject-bmi')) return;
            state.pageError =
              'Based on your height and weight, your BMI is below 23 and you may not be eligible for online treatment. Please speak with your GP.';
            render();
            return;
          }
          state.history.push(state.pageIndex);
          var next = nextVisibleIndex(state.pageIndex);
          if (next !== -1) goTo(next);
          else render();
        });
      });
    });
    root.querySelectorAll('[data-action="unit"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var field = el.getAttribute('data-unit-field');
        var unit = el.getAttribute('data-unit');
        if (field && unit) {
          state.answers[field] = unit;
          if (field === 'height_unit') syncHeightCm();
          if (field === 'weight_unit') syncWeightKg();
          save();
          render();
        }
      });
    });

    root.querySelectorAll('.wma__option[data-option-field]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var field = el.getAttribute('data-option-field');
        var value = el.getAttribute('data-option-value');
        var multi = el.getAttribute('data-option-multi') === '1';
        var page = getPage();
        if (!field || value == null) return;

        if (multi) {
          var vals = Array.isArray(state.answers[field]) ? state.answers[field].slice() : [];
          var idx = vals.indexOf(value);
          var selecting = idx === -1;
          if (selecting) vals.push(value);
          else vals.splice(idx, 1);
          // Exclusive options (e.g. "none", "other") clear every other selection when chosen.
          // Choosing any non-exclusive option removes exclusive ones.
          var exclusiveList = [];
          if (page) {
            if (Array.isArray(page.exclusiveValues)) {
              exclusiveList = page.exclusiveValues.slice();
            } else if (page.exclusiveValue) {
              exclusiveList = [page.exclusiveValue];
            }
          }
          if (exclusiveList.length) {
            var isExclusive = exclusiveList.indexOf(value) !== -1;
            if (selecting && isExclusive) {
              vals = [value];
              if (page.otherValue && exclusiveList.indexOf(page.otherValue) !== -1) {
                if (value === page.otherValue) {
                  state.answers[field + '_other'] = state.answers[field + '_other'] || '';
                } else {
                  state.answers[field + '_other'] = '';
                }
              }
            } else if (selecting) {
              vals = vals.filter(function (v) {
                return exclusiveList.indexOf(v) === -1;
              });
              if (page.otherValue && exclusiveList.indexOf(page.otherValue) !== -1) {
                state.answers[field + '_other'] = '';
              }
            } else if (!selecting && value === page.otherValue) {
              state.answers[field + '_other'] = '';
            }
          }
          state.answers[field] = vals;
          // Drop any staged med-info queue when medication choices change
          if (field === 'other_medical_conditions') state._infoQueue = null;
        } else {
          if (
            page &&
            page.type === 'weightLost' &&
            field === (page.statusField || 'weight_lost_status')
          ) {
            /* Allow check / uncheck of "I did not lose weight" / "I am unsure" */
            if (state.answers[field] === value) {
              state.answers[field] = '';
            } else {
              state.answers[field] = value;
            }
            /* Keep ruler kg for display; API prefers status when set */
            if (!Number(state.answers[page.field || 'weight_lost_on_treatment'])) {
              state.answers[page.field || 'weight_lost_on_treatment'] = '5';
            }
          } else {
            state.answers[field] = value;
          }
        }
        state.pageError = '';
        save();

        if (
          page &&
          page.autoAdvance &&
          page.type === 'single' &&
          pageValid(page) &&
          !(
            (Array.isArray(page.otherValues)
              ? page.otherValues
              : page.otherValue
                ? [page.otherValue]
                : []
            ).indexOf(value) !== -1
          )
        ) {
          var rejectPhaseId = getRejectPhaseId(page);
          if (rejectPhaseId && goToRejectPhase(rejectPhaseId)) {
            return;
          }
          var reject = getReject(page);
          if (reject) {
            state.pageError = reject;
            render();
            return;
          }
          render();
          setTimeout(goNext, 120);
          return;
        }
        render();
      });
    });

    root.querySelectorAll('input[data-field]').forEach(function (el) {
      if (el.type === 'radio' || (el.type === 'checkbox' && el.getAttribute('data-multi'))) {
        return; // handled by .wma__option click
      }
      var handler = function () {
        var field = el.getAttribute('data-field');
        var page = getPage();

        if (el.type === 'checkbox') {
          state.answers[field] = el.checked;
        } else {
          state.answers[field] = el.value;
          if (field === 'height_cm_input' || field === 'height_ft' || field === 'height_in') {
            syncHeightCm();
          }
          if (field === 'weight_kg_input' || field === 'weight_st' || field === 'weight_lb') {
            syncWeightKg();
          }
        }
        state.pageError = '';
        save();

        // Soft-update text fields (avoid full re-render so multi-field fills stay stable)
        if (el.type === 'checkbox') {
          render();
        } else {
          if (field === 'dob_dd' || field === 'dob_mm' || field === 'dob_yyyy') {
            syncDobFeedback();
          } else {
            syncPrimaryButton();
          }
        }
      };
      el.addEventListener(el.type === 'checkbox' ? 'change' : 'input', handler);
    });

    root.querySelectorAll('input[data-other], textarea[data-other]').forEach(function (el) {
      el.addEventListener('input', function () {
        var field = el.getAttribute('data-other');
        state.answers[field + '_other'] = el.value;
        save();
        syncPrimaryButton();
      });
    });
  }

  function syncDobFeedback() {
    var page = getPage();
    if (!page || page.type !== 'dob' || page.fieldPrefix) {
      syncPrimaryButton();
      return;
    }
    var msg = dobWarning(page);
    state.pageError = msg;

    var row = root.querySelector('.wma__dob-row');
    if (!row) {
      syncPrimaryButton();
      return;
    }

    var existing = root.querySelector('[data-dob-alert]');
    if (msg) {
      if (existing) {
        existing.textContent = msg;
      } else {
        var alertEl = document.createElement('p');
        alertEl.className = 'wma__alert wma__alert--error';
        alertEl.setAttribute('role', 'alert');
        alertEl.setAttribute('data-dob-alert', '1');
        alertEl.textContent = msg;
        var fieldset = row.closest('.wma__fieldset');
        if (fieldset && fieldset.parentNode) {
          fieldset.parentNode.insertBefore(alertEl, fieldset.nextSibling);
        } else {
          row.insertAdjacentElement('afterend', alertEl);
        }
      }
      root.querySelectorAll('.wma__dob-row .wma__input-pill').forEach(function (input) {
        input.classList.add('is-invalid');
      });
    } else {
      if (existing) existing.remove();
      root.querySelectorAll('.wma__dob-row .wma__input-pill').forEach(function (input) {
        input.classList.remove('is-invalid');
      });
    }
    syncPrimaryButton();
  }

  function syncPrimaryButton() {
    var btn = root.querySelector('[data-action="next"], [data-action="submit"]');
    if (!btn) return;
    var page = getPage();
    var disabled = !pageValid(page) || state.submitting;
    if (page && page.type === 'dob' && !page.fieldPrefix) {
      disabled = disabled || !!dobWarning(page);
    } else {
      disabled = disabled || !!getReject(page);
    }
    btn.classList.toggle('is-disabled', disabled);
    btn.disabled = disabled;
    if (disabled) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
  }

  function doSubmit() {
    var page = getPage();
    if (!pageValid(page)) {
      state.pageError = validationMessage(page);
      render();
      return;
    }
    if (state.submitSuccess) return;
    state.submitting = true;
    state.submitError = '';
    state.submitSuccess = '';
    render();
    window.WmAssessmentApi.submitAssessment(state, config)
      .then(function () {
        state.submitting = false;
        if (window.WmClEvents) {
          window.WmClEvents.track(
            'wl_complete_assessment',
            Object.assign({ funnel: 'wl' }, window.WmClEvents.identityFromAnswers(state.answers)),
            { once: true }
          );
        }
        clearStorage();
        if (config.disableSuccessRedirect) {
          state.submitted = true;
          render();
          return;
        }
        state.submitSuccess =
          'Your assessment has been submitted successfully. Redirecting you shortly…';
        render();
        var delay =
          typeof config.successRedirectDelayMs === 'number'
            ? config.successRedirectDelayMs
            : 4000;
        window.setTimeout(function () {
          window.location.href = window.WmAssessmentApi.buildSelectProductUrl(config, state);
        }, delay);
      })
      .catch(function (err) {
        state.submitting = false;
        state.submitError = (err && err.message) || 'Network error while submitting assessment.';
        render();
      });
  }

  var api = {
    setUpload: function (key, data) {
      state.uploads[key] = data;
      state.cameraActive = false;
      state.cameraFailed = false;
      state.uploadError = '';
      if (window.WmAssessmentUploads && window.WmAssessmentUploads.stopCamera) {
        window.WmAssessmentUploads.stopCamera();
      }
      save();
      render();
    },
    setUploadProgress: function (key, data) {
      var prev = state.uploads[key] || {};
      var next = Object.assign({}, prev, data || {});
      state.uploads[key] = next;
      if (next.uploading || next.localPreviewUrl || next.progress != null) {
        state.cameraActive = false;
        state.cameraFailed = false;
        if (window.WmAssessmentUploads && window.WmAssessmentUploads.stopCamera) {
          window.WmAssessmentUploads.stopCamera();
        }
      }
      var pct = Math.max(0, Math.min(100, Math.round(Number(next.progress) || 0)));
      var pctEl = root.querySelector('[data-upload-progress="' + key + '"]');
      var barEl = root.querySelector('[data-upload-bar="' + key + '"]');
      var row = root.querySelector('[data-upload-row="' + key + '"]');
      if (pctEl && barEl && row) {
        pctEl.textContent = pct + '%';
        barEl.style.width = pct + '%';
        if (pct > 0) barEl.classList.add('is-complete');
        else barEl.classList.remove('is-complete');
        return;
      }
      state.uploadError = '';
      render();
    },
    setUploadError: function (msg) {
      state.uploadError = msg || '';
      render();
    },
    clearUpload: function (key) {
      if (key) delete state.uploads[key];
      save();
      render();
    },
    getState: function () {
      return state;
    },
    /** Test/debug helpers */
    goToPhase: function (id) {
      var idx = findPhaseIndex(id);
      if (idx >= 0) goTo(idx);
    },
    setAnswers: function (obj) {
      Object.assign(state.answers, obj || {});
      save();
      render();
    },
    clearAll: function () {
      if (window.WmAssessmentUploads && window.WmAssessmentUploads.stopCamera) {
        window.WmAssessmentUploads.stopCamera();
      }
      state = {
        pageIndex: 0,
        answers: {},
        history: [],
        uploads: {},
        guidelinesModal: null,
        rangeModal: null,
        cameraActive: false,
        cameraFailed: false,
        pageError: '',
        uploadError: '',
        submitError: '',
        submitting: false
      };
      clearStorage();
      render();
    }
  };

  window.__wmAssessmentTest = api;

  function init() {
    root = document.getElementById('wm-assessment-app');
    if (!root) return;
    mergeOverrides();
    if (!config || !config.phases) {
      root.innerHTML = '<p class="wma__alert wma__alert--error">Assessment config missing.</p>';
      return;
    }
    // Always restart from step 1 on page load (do not restore saved progress)
    clearStorage();
    state.pageIndex = 0;
    state._lastRenderedPageIndex = null;
    state.answers = {};
    state.history = [];
    state.uploads = {};
    state.guidelinesModal = null;
    state.rangeModal = null;
    state.cameraActive = false;
    state.cameraFailed = false;
    state.pageError = '';
    state.uploadError = '';
    state.submitError = '';
    state.submitting = false;
    if (window.__wmAssessmentOverrides && window.__wmAssessmentOverrides.customerEmailFallback) {
      state.answers.email = window.__wmAssessmentOverrides.customerEmailFallback;
    }
    window.WmAssessmentUploads.bindOnce(api);
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
